import Vue from 'vue'

import { cloneDeep } from 'lodash'

import { Colors, charSets, empty } from '@sigma-frontend-libs/commons'
import { URL_SHORTENER_SHORT_URL_MIN_LENGTH } from '@sigmasms/commons'

import { icons, isEmpty, proxy } from '@/utils'

import { serviceNameViaPoint } from '@/components/services/url-shortener-settings/presets/utils'
import { validationInfoStates } from '@/components/validationInfo'

import render from './render'

export default {
  name: 'GenerationParams',

  mixins: [ proxy({ type: 'object' }) ],

  computed: {
    defaults() {
      return cloneDeep(empty?.urlShortenerSettingsPresets?.create?.generationParams)
    },

    charSets: {
      get() {
        const characters = this.proxy?.generationParams?.characters || ''
        const result = {
          latinLower: characters?.includes(charSets.latinLower) || false,
          latinUpper: characters?.includes(charSets.latinUpper) || false,
          digits: characters?.includes(charSets.digits) || false,
          specials: characters?.includes(charSets.specials) || false
        }

        return result
      },
      set(value) {
        const result = Object.keys(value)
          .filter(key => {
            return value[key]
          })
          .map(key => {
            return charSets[key]
          })
          .join('')
        this.proxy.generationParams.characters = result
      }
    },

    charSetsLabel() {
      if (isEmpty(this.proxy?.generationParams?.characters)) {
        return this.getTranslate(`${serviceNameViaPoint}.hints.charSets.empty`)
      }

      return this.proxy?.generationParams?.characters
    },
    charSetsStickerButton() {
      const result = {
        icon: icons.clear,
        disabled: this.proxy?.generationParams?.characters === this.defaults?.characters,
        callback: () => {
          Vue.set(this.proxy.generationParams, 'characters', this.defaults?.characters)
        },
        tooltip: this.getTranslate(`${serviceNameViaPoint}.tooltips.generationParams.reset`)
      }

      return result
    },

    lengthFilled() {
      return !isEmpty(this.proxy?.generationParams?.length)
    },
    lengthLengthValidation() {
      return this.lengthFilled && Number(this.proxy?.generationParams?.length) >= URL_SHORTENER_SHORT_URL_MIN_LENGTH
    },
    lengthValidation() {
      return this.lengthFilled && this.lengthLengthValidation
    },
    patternFilled() {
      return !isEmpty(this.proxy?.generationParams?.pattern)
    },
    patternLengthValidation() {
      return this.patternFilled && this.proxy?.generationParams?.pattern.length >= URL_SHORTENER_SHORT_URL_MIN_LENGTH
    },
    patternCharactersValidation() {
      const pattern = this.proxy?.generationParams?.pattern
      const regex = new RegExp(`^[${this.proxy?.generationParams?.characters}]*$`)

      if (pattern) {
        return regex.test(pattern)
      }

      return false
    },
    patternValidation() {
      return this.patternFilled && this.patternLengthValidation && this.patternCharactersValidation
    },

    paramsStickerButton() {
      const result = {
        icon: icons.clear,
        disabled: this.proxy?.generationParams?.length === this.defaults?.length || this.proxy?.generationParams?.pattern === this.defaults?.pattern,
        callback: () => {
          Vue.set(this.proxy.generationParams, 'length', this.defaults?.length)
          Vue.set(this.proxy.generationParams, 'pattern', undefined)
        },
        tooltip: this.getTranslate(`${serviceNameViaPoint}.tooltips.generationParams.reset`)
      }

      return result
    },

    charactersFilled() {
      return !isEmpty(this.proxy?.generationParams?.characters)
    },
    allFieldsFilled() {
      return !isEmpty(this.proxy?.generationParams?.length) && !isEmpty(this.proxy?.generationParams?.pattern)
    },

    validationGroups() {
      if (this.allFieldsFilled) {
        return []
      }

      switch (true) {
        case this.lengthFilled: return [
          this.getValidationGroup('length', {
            filled: true,
            length: this.lengthLengthValidation
          })
        ]
        case this.patternFilled: return [
          this.getValidationGroup('pattern', {
            filled: true,
            length: this.patternLengthValidation,
            characters: this.patternCharactersValidation
          })
        ]
        default: return []
      }
    },

    validation() {
      return (this.lengthValidation || this.patternValidation) && (this.charactersFilled && !this.allFieldsFilled)
    },

    $_label() {
      if (this.allFieldsFilled) {
        return this.getTranslate(`${serviceNameViaPoint}.hints.generationParams.allFilled`)
      }

      const generationParams = this.proxy?.generationParams

      let field
      if (!isEmpty(generationParams.length)) {
        field = 'length'
      } else if (!isEmpty(generationParams.pattern)) {
        field = 'pattern'
      }
      if (!field) {
        return this.getTranslate(`${serviceNameViaPoint}.hints.generationParams.empty`)
      }

      return this.getTranslate(
        `${serviceNameViaPoint}.hints.generationParams.templates.${field}`,
        { [field]: generationParams[field] }
      )
    },
    $_color() {
      switch (true) {
        case this.allFieldsFilled: return Colors.error
        case this.validation: return Colors.success
        default: return Colors.error
      }
    }
  },

  watch: {
    validation() {
      this.$emit('validation', this.validation)
    }
  },

  mounted() {
    this.$emit('validation', this.validation)
  },

  methods: {
    getValidationGroup(field, rules) {
      return {
        title: this.getTranslate(`${serviceNameViaPoint}.labels.generationParams.${field}`),
        rules: Object.entries(rules).map(([ rule, valid ]) => {
          let state = validationInfoStates.invalid
          if (valid) {
            state = validationInfoStates.valid
          }

          return {
            text: this.getTranslate(`${serviceNameViaPoint}.hints.generationParams.validation.${field}.${rule}`, { min: URL_SHORTENER_SHORT_URL_MIN_LENGTH }),
            state,
            html: true
          }
        })
      }
    },

    updateCharset(type, value) {
      this.charSets = {
        ...this.charSets,
        [type]: value
      }
    }
  },

  render
}
